import {
  CurecallTag,
  type CurecallTagProps,
  CurecallTooltip,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { useQueryClient } from '@tanstack/react-query'
import { twMerge } from 'src/utils/tw-merge.util'
import { useTranslation } from 'react-i18next'
import { getFormattedDateWithHours } from '../../utils/date-formatter.util'
import { useNotification } from 'src/providers/notification.provider'
import { TPatientMedAlert, MED_ALERT_UI_TO_TAG_STATUS } from 'src/types/med-alert'
import { PatientService } from 'src/services/patient.service'
import { useCurrentProject } from 'src/providers/current-project.provider'

export type Props = {
  medicalAlertList: TPatientMedAlert[]
  medAlertSize?: CurecallTagProps['size']
  className?: string
  showPinnedOnly?: boolean
  isWorkflow?: boolean
  callback?: (event: React.MouseEvent) => void
}

const MedicalAlertList = ({
  medicalAlertList,
  medAlertSize,
  className,
  showPinnedOnly,
  isWorkflow,
  callback,
}: Props) => {
  const { project } = useCurrentProject()
  const { notify } = useNotification()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const getSortedMedAlertList = (medicalAlertList: TPatientMedAlert[]) => {
    const res = showPinnedOnly
      ? medicalAlertList.filter((medAlert) => medAlert.pinned === true)
      : medicalAlertList
    return res.sort((a, b) => a.id - b.id)
  }

  const isMedAlertDeletable = (medicalAlert: TPatientMedAlert): boolean => {
    const isMedAlertTypeDeletable = ['MEDICOLEGAL', 'TODO'].includes(medicalAlert.MedAlert.type)
    return isMedAlertTypeDeletable
  }

  const handleRemovePatientMedAlert = async (patientId: number, patientMedAlertId: number) => {
    try {
      await PatientService.deletePatientMedAlerts(project.id, patientId, patientMedAlertId)
      queryClient.invalidateQueries()
    } catch (err: any) {
      notify({
        title: t('common.actions.error'),
        message: err,
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    }
  }

  if (!medicalAlertList?.length) return null

  return (
    <div className={twMerge('c-flex c-flex-wrap c-gap-4', className)}>
      {getSortedMedAlertList(medicalAlertList)?.map((patientMedAlert: TPatientMedAlert) => (
        <CurecallTooltip
          key={patientMedAlert.id}
          content={getFormattedDateWithHours(t, patientMedAlert.createdAt)}
          tooltipPosition="bottom-start"
          contentWrapperClassName="tw-w-max"
        >
          <CurecallTag
            status={MED_ALERT_UI_TO_TAG_STATUS[patientMedAlert.MedAlert.ui]}
            size={medAlertSize}
            isDeletable={isMedAlertDeletable(patientMedAlert)}
            isWorkflow={isWorkflow}
            onDelete={(event) => {
              callback && callback(event)
              isMedAlertDeletable(patientMedAlert) &&
                handleRemovePatientMedAlert?.(patientMedAlert.PatientId, patientMedAlert.id)
            }}
          >
            {patientMedAlert.MedAlert.label}
          </CurecallTag>
        </CurecallTooltip>
      ))}
    </div>
  )
}

export default MedicalAlertList
